import Point from "./point";
import { Size, Length } from "./states";

interface CancerSurface {
  unit: number;
  percent: number;
}

export class AnalysisResult {
  private readonly stomachOutline: number;
  private readonly cancerOutlines: Array<CancerSurface>;
  public readonly unit: string;

  constructor(
    stomachOutline: number,
    cancerOutlines: Array<CancerSurface>,
    unit: string
  ) {
    this.stomachOutline = stomachOutline;
    this.cancerOutlines = cancerOutlines;
    this.unit = unit;
  }

  public get stomachOutlineInUnit() {
    return this.stomachOutline.toFixed(2);
  }

  public get cancerOutlinesSet() {
    return this.cancerOutlines.map(value => {
      return {
        unit: value.unit.toFixed(2),
        percent: (value.percent * 100).toFixed(2)
      };
    });
  }

  public get cancerSurfaceSummary() {
    return this.cancerOutlines
      .reduce((sum, current) => sum + current.unit, 0)
      .toFixed(2);
  }

  public get cancerSurfaceRatio() {
    return (
      this.cancerOutlines.reduce((sum, current) => sum + current.percent, 0) *
      100
    ).toFixed(2);
  }
}

export class AnalysisData {
  private stomach: Array<Point> = [];
  private cancerOutlines: Array<Array<Point>> = [];
  private cancerOutlinesCounter = -1;
  public rulerRealLength?: Length;
  public size?: Size;
  public ruler?: { start: Point; end: Point };

  public get stomachOutline() {
    return this.stomach;
  }

  public get cancerOutline() {
    return this.cancerOutlines[this.cancerOutlinesCounter];
  }

  public get completedCancerOutlines() {
    return this.cancerOutlines.slice(0, this.cancerOutlinesCounter);
  }

  public get rulerLength() {
    if (this.ruler) {
      const a = Math.abs(this.ruler?.end.x - this.ruler?.start.x);
      const b = Math.abs(this.ruler?.end.y - this.ruler?.start.y);
      return Math.hypot(a, b);
    }
  }

  public get cancerIndex() {
    return this.cancerOutlinesCounter;
  }

  public addCancerOutline() {
    this.cancerOutlines.push([]);
    this.cancerOutlinesCounter += 1;
    return this.cancerOutlinesCounter;
  }

  public generateReport() {
    const unitName = this.rulerRealLength?.cm == false ? "in²" : "cm²";
    const stomachArea = this.calculateSurface(this.stomach);
    if (stomachArea) {
      const areas: Array<CancerSurface> = [];
      this.cancerOutlines.forEach(points => {
        const area = this.calculateSurface(points);
        if (area) {
          const percent = area / stomachArea;
          areas.push({ unit: area, percent: percent });
        }
      });
      return new AnalysisResult(stomachArea, areas, unitName);
    }
  }

  private calculateSurface(points: Array<Point>) {
    if (this.rulerLength && this.rulerRealLength) {
      const area = this.calcPolygonArea(points);
      return (
        (area * Math.pow(this.rulerRealLength.length, 2)) /
        Math.pow(this.rulerLength, 2)
      );
    }
  }

  private calcPolygonArea(vertices: Array<Point>) {
    let total = 0;

    for (let i = 0, l = vertices.length; i < l; i++) {
      const addX = vertices[i].x;
      const addY = vertices[i === vertices.length - 1 ? 0 : i + 1].y;
      const subX = vertices[i === vertices.length - 1 ? 0 : i + 1].x;
      const subY = vertices[i].y;

      total += addX * addY * 0.5;
      total -= subX * subY * 0.5;
    }

    return Math.abs(total);
  }
}
